/* eslint-disable max-lines */
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { isEmpty, isUndefined, Merge, XOR } from '~libs.typescript';
import { protectFeature } from '~libs.shared.system';

import {
   AdditionalEntitlement,
   MedicinePrescriptionReimbursement,
   Reimbursement,
   ReimbursementIndication,
   ReimbursementType,
   VisitDiagnoses,
} from '../types';

import {
   isEntitlementAllowsForReimbursementFree,
   isEntitlementRestrictedForReimbursementFreeAndFull,
} from './additionalEntitlements';

const REIMBURSEMENT_FULL = '100%';
const REIMBURSEMENT_HALF = '50%';
const REIMBURSEMENT_THIRTY_PERCENT = '30%';
const REIMBURSEMENT_FLAT_RATE = 'R';
const REIMBURSEMENT_FREE = 'B';
const REIMBURSEMENT_SENIORITY = 'S';
const REIMBURSEMENT_CHILDHOOD = 'DZ';
const REIMBURSEMENT_PREGNANCY = 'C';

const PRESCRIPTION_REIMBURSEMENT_TO_REIMBURSEMENT_MAPPER = {
   code: {
      B: REIMBURSEMENT_FREE,
      FULL: REIMBURSEMENT_FULL,
      HALF: REIMBURSEMENT_HALF,
      R: REIMBURSEMENT_FLAT_RATE,
      THIRTY_PERCENT: REIMBURSEMENT_THIRTY_PERCENT,
   },
   priceDescription: {
      B: 'Bezpłatne',
      FULL: 'Pełna',
      HALF: 'Częściowa',
      R: 'Ryczałt',
      THIRTY_PERCENT: 'Częściowa',
   },
};

const ELIGIBLE_REIMBURSEMENT_CODES = [
   REIMBURSEMENT_FLAT_RATE,
   REIMBURSEMENT_FREE,
   REIMBURSEMENT_HALF,
   REIMBURSEMENT_THIRTY_PERCENT,
] as const;

const STATUTORILY_DESIGNATED_NOT_FULL_ENTITLEMENTS = [
   REIMBURSEMENT_SENIORITY,
   REIMBURSEMENT_CHILDHOOD,
   REIMBURSEMENT_PREGNANCY,
] as const;

export const mapPrescriptionReimbursementToReimbursement = (
   medicinePrescriptionreimbursement: MedicinePrescriptionReimbursement
): Reimbursement => ({
   code: PRESCRIPTION_REIMBURSEMENT_TO_REIMBURSEMENT_MAPPER.code[medicinePrescriptionreimbursement.type],
   priceDescription:
      PRESCRIPTION_REIMBURSEMENT_TO_REIMBURSEMENT_MAPPER.priceDescription[medicinePrescriptionreimbursement.type],
   reimbursementIndication: medicinePrescriptionreimbursement.reimbursementIndication,
});

export const findReimbursementByType = (
   reimbursementType: ReimbursementType,
   reimbursements: MedicinePrescriptionReimbursement[]
): Reimbursement => {
   const foundReimbursement = reimbursements.find((reimbursement) => reimbursement.type === reimbursementType);

   if (!foundReimbursement) {
      return findReimbursementByType('FULL', reimbursements)!;
   }

   return mapPrescriptionReimbursementToReimbursement(foundReimbursement);
};

export const mapReimbursementToPrescriptionReimbursementType = (
   reimbursement: Reimbursement | null
): ReimbursementType | null => {
   if (!reimbursement) {
      return null;
   }

   switch (reimbursement.code) {
      case REIMBURSEMENT_FREE:
         return 'B';

      case REIMBURSEMENT_HALF:
         return 'HALF';
      case REIMBURSEMENT_THIRTY_PERCENT:
         return 'THIRTY_PERCENT';
      case REIMBURSEMENT_FLAT_RATE:
         return 'R';
      case REIMBURSEMENT_PREGNANCY:
      case REIMBURSEMENT_SENIORITY:
      case REIMBURSEMENT_CHILDHOOD:
         return null;
      case REIMBURSEMENT_FULL:
      default:
         return 'FULL';
   }
};

export const reimbursementFinderCreator = (prescriptionReimbursements: MedicinePrescriptionReimbursement[]) => (
   prescriptionReimbursementType: ReimbursementType | null | undefined
): Reimbursement | null => {
   const foundReimbursement = prescriptionReimbursements.find(
      (reimbursement) => reimbursement.type === prescriptionReimbursementType
   );

   if (!foundReimbursement) {
      return null;
   }

   return mapPrescriptionReimbursementToReimbursement(foundReimbursement);
};

export const availableReimbursementFinderCreator = (
   prescriptionReimbursements: MedicinePrescriptionReimbursement[]
) => (excludedPrescriptionReimbursements: ReimbursementType[] | null | undefined): Reimbursement | null => {
   const foundReimbursements = prescriptionReimbursements.find(
      (reimbursement) => !excludedPrescriptionReimbursements?.includes(reimbursement.type)
   );

   if (!foundReimbursements) {
      return null;
   }

   return mapPrescriptionReimbursementToReimbursement(foundReimbursements);
};

export const hasReimbursementFull = (reimbursement: Reimbursement | null): boolean => {
   return reimbursement?.code === REIMBURSEMENT_FULL;
};

export const hasReimbursementFree = (reimbursement: Reimbursement | null): boolean => {
   return reimbursement?.code === REIMBURSEMENT_FREE;
};

const isSingleLetterGroupIcd10Code = (icd10Code: string): boolean => icd10Code.length === 1;

const matchesEligibleIcd10Code = (visitIcd10Code: string, eligibleIcd10Code: string): boolean =>
   isSingleLetterGroupIcd10Code(eligibleIcd10Code)
      ? visitIcd10Code.startsWith(eligibleIcd10Code)
      : visitIcd10Code === eligibleIcd10Code;

export const shouldDistinctReimbursementByVisitDiagnoses = (
   reimbursementEligibleIcd10Codes: string[],
   visitDiagnoses: VisitDiagnoses
): boolean =>
   reimbursementEligibleIcd10Codes.some((eligibleIcd10Code) =>
      visitDiagnoses.some((diagnosis) => matchesEligibleIcd10Code(diagnosis.icd10Code, eligibleIcd10Code))
   );

export const shouldDistinctReimbursementByChosenEntitlementTypes = (
   reimbursementEligibleEntitlement: string | undefined,
   selectedAdditionalEntitlements: AdditionalEntitlement[]
): boolean => {
   if (!reimbursementEligibleEntitlement) {
      return false;
   }
   return selectedAdditionalEntitlements.some(
      ({ additionalEntitlementType }) => additionalEntitlementType === reimbursementEligibleEntitlement
   );
};

export const formatReimbursement = (reimbursement: Reimbursement): string => {
   return `${reimbursement.code} - ${reimbursement.priceDescription}`;
};

type SelectReimbursementParameters = Merge<
   { reimbursements: MedicinePrescriptionReimbursement[] },
   XOR<
      [
         {
            reimbursement: Reimbursement | null;
         },
         {
            selectedReimbursement: Reimbursement | undefined;
            additionalEntitlements: AdditionalEntitlement[];
         }
      ]
   >
>;

export const selectReimbursement = (parameters: SelectReimbursementParameters): Reimbursement => {
   const findReimbursement = reimbursementFinderCreator(parameters.reimbursements);
   if ('reimbursement' in parameters && parameters.reimbursement) {
      return (
         findReimbursement(mapReimbursementToPrescriptionReimbursementType(parameters.reimbursement)) ??
         findReimbursement('FULL')!
      );
   }

   if ('selectedReimbursement' in parameters && parameters.selectedReimbursement) {
      const findAvailableReimbursement = availableReimbursementFinderCreator(parameters.reimbursements);

      const hasRestrictedEntitlementsForFreeAndFull = parameters.additionalEntitlements.some((entitlement) =>
         isEntitlementRestrictedForReimbursementFreeAndFull(entitlement.additionalEntitlementType)
      );

      if (hasRestrictedEntitlementsForFreeAndFull) {
         const patientReimbursementSelectedByUser = parameters.selectedReimbursement;
         const reimbursementType = patientReimbursementSelectedByUser
            ? mapReimbursementToPrescriptionReimbursementType(patientReimbursementSelectedByUser)
            : undefined;

         if (
            !patientReimbursementSelectedByUser ||
            (reimbursementType && !isEntitlementRestrictedForReimbursementFreeAndFull(reimbursementType))
         ) {
            return findReimbursement(reimbursementType) ?? findReimbursement('FULL')!;
         }

         if (parameters.additionalEntitlements.some(isEntitlementAllowsForReimbursementFree)) {
            return findReimbursement('B') ?? findReimbursement('FULL')!;
         }

         return findAvailableReimbursement(['FULL', 'B'])!;
      }

      if (parameters.selectedReimbursement) {
         return findReimbursement(mapReimbursementToPrescriptionReimbursementType(parameters.selectedReimbursement))!;
      }

      if (parameters.additionalEntitlements.some(isEntitlementAllowsForReimbursementFree)) {
         return findReimbursement('B') ?? findReimbursement('FULL')!;
      }
   }

   return findReimbursement('FULL')!;
};

export const isStatutorilyDesignatedNonFullEntitlementsOnly = (
   entitlements: AdditionalEntitlement[]
): entitlements is Array<
   Omit<AdditionalEntitlement, 'additionalEntitlementType'> & { additionalEntitlementType: 'S' | 'DZ' | 'C' }
> =>
   Boolean(
      !isEmpty(entitlements) &&
         entitlements.every((entitlement) =>
            STATUTORILY_DESIGNATED_NOT_FULL_ENTITLEMENTS.some(
               (entitlementType) => entitlementType === entitlement.additionalEntitlementType
            )
         )
   );

export const hasEligibleReimbursementCode = (
   reimbursementCode?: string
): reimbursementCode is typeof ELIGIBLE_REIMBURSEMENT_CODES[number] => {
   return reimbursementCode ? ELIGIBLE_REIMBURSEMENT_CODES.some((code) => code === reimbursementCode) : false;
};
const isIndicationValidByVisitDiagnoses = (
   indication: ReimbursementIndication,
   visitDiagnoses: VisitDiagnosis[]
): boolean =>
   isEmpty(indication.eligibleIcd10Codes) ||
   indication.eligibleIcd10Codes.some((eligibleCode) =>
      visitDiagnoses.some(({ icd10Code }) => matchesEligibleIcd10Code(icd10Code, eligibleCode))
   );

export const createStatutorilyDesignatedNonFullEntitlementIndicationValidChecker = (
   visitDiagnoses: VisitDiagnosis[]
) => (indication: ReimbursementIndication): boolean => {
   const hasStatutorilyDesignatedIndicationWithNonFullEntitlement = Boolean(
      indication.statutorilyDesignatedFreeEntitlements?.some((entitlement) =>
         STATUTORILY_DESIGNATED_NOT_FULL_ENTITLEMENTS.some((entitlementType) => entitlementType === entitlement)
      )
   );

   return (
      hasStatutorilyDesignatedIndicationWithNonFullEntitlement &&
      isIndicationValidByVisitDiagnoses(indication, visitDiagnoses)
   );
};

export const createReimbursementIndicationValidChecker = (
   visitDiagnoses: VisitDiagnoses,
   selectedAdditionalEntitlements: AdditionalEntitlement[]
) => (indication: ReimbursementIndication): boolean => {
   const validByChosenEntitlements =
      !indication.eligibleEntitlement ||
      selectedAdditionalEntitlements.some(
         (entitlement) => entitlement.additionalEntitlementType === indication.eligibleEntitlement
      );

   return (
      (isIndicationValidByVisitDiagnoses(indication, visitDiagnoses) && validByChosenEntitlements) ||
      isStatutorilyDesignatedNonFullEntitlementsOnly(selectedAdditionalEntitlements)
   );
};

type ReimbursementIndicationDistinction = {
   distinctByVisitDiagnoses: boolean;
   distinctByChosenEntitlements: boolean;
};

export const getReimbursementIndicationDistinction = (
   indication: ReimbursementIndication,
   visitDiagnoses: VisitDiagnoses,
   selectedAdditionalEntitlements: AdditionalEntitlement[]
): ReimbursementIndicationDistinction => {
   const distinctByVisitDiagnoses = protectFeature(
      'extendedMedicineDatabase',
      shouldDistinctReimbursementByVisitDiagnoses(indication.eligibleIcd10Codes, visitDiagnoses),
      false
   );

   const distinctByChosenEntitlements = protectFeature(
      'extendedMedicineDatabase',
      shouldDistinctReimbursementByChosenEntitlementTypes(
         indication.eligibleEntitlement,
         selectedAdditionalEntitlements
      ),
      false
   );

   return {
      distinctByVisitDiagnoses: distinctByVisitDiagnoses,
      distinctByChosenEntitlements: distinctByChosenEntitlements,
   };
};

export const hasReimbursementIndicationDistinct = (
   indications: ReimbursementIndication[] | undefined,
   visitDiagnoses: VisitDiagnoses,
   selectedAdditionalEntitlements: AdditionalEntitlement[]
): boolean => {
   if (isUndefined(indications)) {
      return false;
   }

   return indications.some((indication) => {
      const { distinctByChosenEntitlements, distinctByVisitDiagnoses } = getReimbursementIndicationDistinction(
         indication,
         visitDiagnoses,
         selectedAdditionalEntitlements
      );

      return distinctByChosenEntitlements || distinctByVisitDiagnoses;
   });
};
